import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { bookingAPI } from "@/lib/api";
import { toast } from "sonner";
import { 
  Package, 
  BookOpen,
  Plane,
  Loader2,
  ArrowRight,
  Phone,
  Mail,
  Calendar,
  RefreshCw,
  X
} from "lucide-react";

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await bookingAPI.getMyBookings();
      setBookings(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch bookings:', error.message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchBookings();
    toast.success("Bookings refreshed");
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }
    
    setCancelling(bookingId);
    try {
      await bookingAPI.cancelBooking(bookingId);
      toast.success("Booking cancelled successfully");
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel booking");
    } finally {
      setCancelling(null);
    }
  };

  const canCancel = (status) => {
    return status === "pending_payment" || status === "confirmed";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending_payment': return 'bg-amber-100 text-amber-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight">My Bookings</h1>
        <Button 
          variant="outline" 
          onClick={handleRefresh}
          disabled={refreshing}
          className="rounded-none gap-2"
          data-testid="refresh-bookings-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-10 border border-border">
          <BookOpen className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground mb-4">You haven't made any bookings yet</p>
          <Link to="/browse">
            <Button className="rounded-none" data-testid="start-booking-btn">
              Browse Listings
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div key={booking.id} className="border border-border">
              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 flex items-center justify-center ${
                      booking.listing_type === 'jet' ? 'bg-amber-500/10' : 'bg-orange-500/10'
                    }`}>
                      {booking.listing_type === 'jet' ? (
                        <Plane className="w-6 h-6 text-amber-600" />
                      ) : (
                        <Package className="w-6 h-6 text-orange-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 font-bold text-lg">
                        <span>{booking.listing_details.origin_city}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <span>{booking.listing_details.destination_city}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        by {booking.listing_details.partner_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-xs font-bold uppercase ${getStatusColor(booking.booking_status)}`}>
                      {booking.booking_status?.replace('_', ' ')}
                    </span>
                    {canCancel(booking.booking_status) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleCancel(booking.id)}
                        disabled={cancelling === booking.id}
                        className="rounded-none"
                        data-testid={`cancel-booking-${booking.id}`}
                      >
                        {cancelling === booking.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="label-text mb-1">Type</p>
                    <p className="font-medium capitalize">{booking.listing_type}</p>
                  </div>
                  <div>
                    <p className="label-text mb-1">Date</p>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{booking.listing_details.departure_date}</span>
                    </div>
                  </div>
                  <div>
                    <p className="label-text mb-1">Quantity</p>
                    <p className="font-medium">
                      {booking.quantity} {booking.listing_type === 'cargo' ? 'kg' : 'seats'}
                    </p>
                  </div>
                  <div>
                    <p className="label-text mb-1">Booked On</p>
                    <p className="font-medium">
                      {new Date(booking.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <p className="label-text mb-2">Partner Contact</p>
                  <div className="flex flex-wrap gap-4">
                    <a 
                      href={`tel:${booking.partner_contact_phone}`}
                      className="flex items-center gap-2 text-sm hover:text-accent transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      {booking.partner_contact_phone}
                    </a>
                    <a 
                      href={`mailto:${booking.partner_contact_email}`}
                      className="flex items-center gap-2 text-sm hover:text-accent transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {booking.partner_contact_email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
